
import { Component, Vue, Ref } from 'vue-property-decorator'
import { registerNiceModalComponent, parseJson, mapTopicTipProps } from '@xt/client/utils/messageModalQueue/index'
import type { TopicTipProps, GiftProps, CouponProps, IMessageResp } from '@xt/client/utils/messageModalQueue/types.d.ts'
import { EnumModalQueueItemType, EnumRegisterModalNames, EnumModalQueueItemCode } from '@xt/client/enums/messageModalQueue/index'
import { Observer } from 'mobx-vue'
import type { BaseCouponItem } from '@xt/client/types/coupon'
import { EnumApiNotice } from '@xt/client/api/notice'
import { $service } from '@xt/client/experimental/request/service'

registerNiceModalComponent()

@Observer
@Component({
  components: {}
})
export default class MessageModalQueue extends Vue {
  hasReadList = new Set<string>()

  transformOrderCouponProps(data: IMessageResp) {
    const coupons = parseJson<Array<BaseCouponItem & { issueResult: boolean }>>(data)

    if (!coupons || !coupons.length) return null

    const successList = coupons.filter(i => i.issueResult)
    const failList = coupons.filter(i => !i.issueResult)

    return {
      successList,
      failList,
      type: EnumModalQueueItemType.Coupon
    }
  }

  transformCouponProps(dataSource: IMessageResp[]): CouponProps | null {
    const successList = dataSource
      .filter(i => i.noticeSubType === EnumModalQueueItemCode.Coupon)
      .map(i => parseJson<BaseCouponItem>(i))
      .filter(i => i !== null)

    if (successList.length === 0) return null

    return {
      successList,
      failList: [],
      type: EnumModalQueueItemType.Coupon
    }
  }

  transformGiftProps(data: IMessageResp): GiftProps | null {
    const parsed = parseJson<GiftProps>(data)

    if (!parsed) return null

    return {
      ...parsed,
      type: EnumModalQueueItemType.Gift
    }
  }

  transformTopicTipProps(data: IMessageResp, code: EnumModalQueueItemCode): TopicTipProps | null {
    const parsed = parseJson<{ regionName: string; guildName: string }>(data)

    if (!parsed) return null

    const dataMap = mapTopicTipProps(code, parsed, this.$store.$global.static)

    // 匹配不到的类型不加入队列
    if (!dataMap) return null

    return {
      bizId: data.bizId,
      ...dataMap
    }
  }

  /** 拉取消息生成弹窗队列 */
  async initCongratulateModal() {
    if (!this.$store.$storeUser.UserInfo.id) return

    const res = await this.$store.$storeUser.messageNotice.onLoadPop()

    if (!res.length) return

    for (const item of res) {
      // 已读消息跳过
      if (this.hasReadList.has(item.id)) continue

      if ([EnumModalQueueItemCode.Init, EnumModalQueueItemCode.Recruit, EnumModalQueueItemCode.Exit].includes(item.noticeSubType)) {
        const queueItem = this.transformTopicTipProps(item, item.noticeSubType)

        if (!queueItem) continue

        this.onReadNotice([item.id])
        await this.$niceModal.show(EnumRegisterModalNames.Topic, queueItem)
      } else if (item.noticeSubType === EnumModalQueueItemCode.Gift) {
        const dataSource = this.transformGiftProps(item)

        if (!dataSource) continue

        this.onReadNotice([item.id])
        await this.$niceModal.show(EnumRegisterModalNames.Gift, {
          dataSource
        })
      } else if (item.noticeSubType === EnumModalQueueItemCode.Coupon) {
        const data = this.transformCouponProps(res)

        if (!data) continue

        const ids = res.filter(i => i.noticeSubType === EnumModalQueueItemCode.Coupon).map(i => i.id)
        this.onReadNotice(ids)
        await this.$niceModal.show(EnumRegisterModalNames.Coupon, {
          data
        })
      } else if (item.noticeSubType === EnumModalQueueItemCode.OrderCoupon) {
        const data = this.transformOrderCouponProps(item)

        this.onReadNotice([item.id]) // 解析失败也标记已读

        if (!data) continue

        await this.$niceModal.show(EnumRegisterModalNames.Coupon, {
          data
        })
      }

      await this.$sleep(100) // 可选短延迟以避免 UI 闪烁
    }
  }

  async onReadNotice(ids: string[]) {
    ids.forEach(id => this.hasReadList.add(id)) // 乐观标记

    const [, resp] = await $service<boolean>({
      url: EnumApiNotice.noticeBatchRead,
      method: 'post',
      params: {
        ids: ids.join(',')
      }
    })

    if (!resp) return

    return resp === true
  }

  checkIsValidPage() {
    return ['index', 'my'].includes((this.$route.name ?? '').toString())
  }

  async created(): Promise<void> {
    if (!this.checkIsValidPage()) return

    await this.initCongratulateModal()
  }

  destroyed(): void {}
}
